/**
 * Events → Event & activity support (1.5.1): each activity with its coming dates. A date not sent yet offers Send to
 * available volunteers (and Send to everyone for a hard-to-fill date); then who pressed "I can help", in the order
 * they pressed — Confirm is the office's press and never goes past the cap; past the cap is standby. A closed day is
 * marked, never offered. After the day: Record hours (an ABF record only — never Housekeeping, never VISIONS).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lengthWords } from '@shared/dayplan';
import type { ActivityDateView, ActivityView } from '@shared/activities';
import { minutesLabel } from '@shared/volunteers';
import { serviceDateKey } from '@shared/dates';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { formatDayMedium } from '@/lib/dates';
import { pressActivity, useActivities, useActivityRefresh } from './api';
import { weekdayName } from './NewActivitySheet';
import { OFFER_NEXT_WORDS } from '@shared/standby';

export function activityWhen(a: Pick<ActivityView, 'schedule' | 'minutes' | 'duration'>, t: (k: string, o?: Record<string, unknown>) => string, lang: string): string {
  return a.schedule.kind === 'weekly'
    ? t('activities.everyWeekday', { weekday: weekdayName(a.schedule.weekday, lang), time: minutesLabel(a.minutes), length: lengthWords(a.duration) })
    : t('activities.onDay', { day: formatDayMedium(a.schedule.dayKey, lang), time: minutesLabel(a.minutes), length: lengthWords(a.duration) });
}

export function ActivitiesSection({ todayKey }: { todayKey: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const list = useActivities();
  const [hours, setHours] = useState<{ a: ActivityView; d: ActivityDateView } | null>(null);
  const acts = list.data?.activities ?? [];
  const es = lang === 'es';

  return (
    <Card as="section" aria-labelledby="h-activities" style={{ marginTop: 18 }} data-testid="activities">
      <CardHeader>
        <CardTitle id="h-activities">{t('activities.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="help">{t('activities.lead')}</p>
        {list.isLoading ? <p role="status">{t('app.loading')}</p> : null}
        {list.data && !acts.length ? (
          <p className="help calm" data-testid="activities-none">
            {t('activities.none')}
          </p>
        ) : null}
        {acts.map((a) => (
          <section key={a.activityId} className="activity" aria-labelledby={`h-${a.activityId}`} data-testid={`activity-${a.activityId}`}>
            <h3 id={`h-${a.activityId}`}>{a.name}</h3>
            <p className="help">
              {activityWhen(a, t, lang)} · {a.where} · {t('activities.capWords', { count: a.cap })} · {a.serviceWords ? a.serviceWords.map((w) => (es ? w.es : w.en)).join(', ') : a.services.map((s) => t(`vol.service.${s}`)).join(', ')}
            </p>
            <p>{a.what}</p>
            {a.forTenant ? (
              <p className="help" data-testid={`activity-tenant-${a.activityId}`}>
                {t('svc183.act.forTenant', { name: a.forTenant.name, unit: a.forTenant.unit })}
              </p>
            ) : null}
            <ul className="needs activity-dates">
              {a.dates.map((d) => (
                <DateRow key={d.dayKey} a={a} d={d} todayKey={todayKey} onHours={() => setHours({ a, d })} />
              ))}
            </ul>
          </section>
        ))}
      </CardContent>
      <Sheet open={!!hours} onOpenChange={(o) => (!o ? setHours(null) : undefined)}>
        {hours ? <HoursSheet a={hours.a} d={hours.d} onClose={() => setHours(null)} /> : null}
      </Sheet>
    </Card>
  );
}

function DateRow({ a, d, todayKey, onHours }: { a: ActivityView; d: ActivityDateView; todayKey: string; onHours: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useActivityRefresh();
  const [busy, setBusy] = useState(false);
  /** 1.8.3: "Can't make it" asks once more before it takes someone off the day. */
  const [sure, setSure] = useState<string | null>(null);
  const past = d.dayKey < todayKey;
  const day = formatDayMedium(d.dayKey, lang);
  const words = OFFER_NEXT_WORDS[lang === 'es' ? 'es' : 'en'];
  const standby = d.standby ?? [];
  const left = d.placesLeft ?? 0;
  const nextUp = standby.find((s) => !s.offeredAt);

  const run = async (body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await pressActivity<{ message: string }>(a.activityId, body);
      toast(r.message);
      announce(r.message);
      refresh();
    } catch (e) {
      const m = errorText(e);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  if (d.closed)
    return (
      <li data-testid={`adate-${d.dayKey}`}>
        <span className="status cancelled">
          <Icon name="lock" aria-hidden="true" /> {t('activities.closedShort')}
        </span>
        <div>
          <b>{day}</b>
          <span>{t('activities.closed', { reason: d.closed.reason })}</span>
        </div>
      </li>
    );

  return (
    <li data-testid={`adate-${d.dayKey}`}>
      <span className={`status ${d.confirmed >= a.cap ? 'completed' : d.eventId ? 'scheduled' : 'proposed'}`}>{t('activities.confirmedOf', { confirmed: d.confirmed, cap: a.cap })}</span>
      <div>
        <b>{day}</b>
        <span>{d.eventId ? t('activities.asked', { count: d.sentTo }) : t('activities.notSent')}</span>
        {d.helpers.length ? (
          <ul className="helpers" aria-label={t('activities.helpersOn', { day })}>
            {d.helpers.map((h) => (
              <li key={h.volunteerId} data-testid={`helper-${d.dayKey}-${h.volunteerId}`}>
                <span>
                  {h.name} — <i>{t(`activities.state.${h.state}`)}</i>
                </span>
                {/* 1.8.3: someone on standby who was offered the place may be confirmed by the office too (a volunteer with no email is called) */}
                {(h.state === 'helping' || (h.state === 'standby' && left > 0 && standby.some((s) => s.volunteerId === h.volunteerId && s.offeredAt))) && !past ? (
                  <Button size="sm" disabled={busy} onClick={() => run({ what: 'confirm', confirm: { dayKey: d.dayKey, volunteerId: h.volunteerId } })} data-testid={`act-confirm-${d.dayKey}-${h.volunteerId}`}>
                    {t('activities.confirm', { name: h.name })}
                  </Button>
                ) : null}
                {h.state !== 'standby' && !past && !d.closedOut ? (
                  sure === h.volunteerId ? (
                    <span className="svc183-sure" role="group" aria-label={t('svc183.act.releaseSure', { name: h.name })} data-testid={`act-release-sure-${d.dayKey}-${h.volunteerId}`}>
                      <span>{t('svc183.act.releaseSure', { name: h.name })}</span>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setSure(null);
                          void run({ what: 'release', release: { dayKey: d.dayKey, volunteerId: h.volunteerId } });
                        }}
                        data-testid={`act-release-yes-${d.dayKey}-${h.volunteerId}`}
                      >
                        {t('svc183.act.releaseYes')}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setSure(null)}>
                        {t('svc183.act.releaseNo')}
                      </Button>
                    </span>
                  ) : (
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => setSure(h.volunteerId)} aria-label={t('svc183.act.releaseLabel', { name: h.name })} data-testid={`act-release-${d.dayKey}-${h.volunteerId}`}>
                      {t('svc183.act.release')}
                    </Button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {standby.length && !past ? (
          <div className="svc183-standby" data-testid={`standby-${d.dayKey}`}>
            <b id={`standby-h-${a.activityId}-${d.dayKey}`}>{words.title}</b> <span className="help">· {words.order}</span>
            <ol aria-label={t('svc183.act.standbyFor', { day })}>
              {standby.map((s) => (
                <li key={s.volunteerId} data-testid={`standby-${d.dayKey}-${s.volunteerId}`}>
                  {s.name}
                  {s.offeredAt ? <span className="help"> — {t('svc183.act.offeredOn', { when: formatDayMedium(serviceDateKey(new Date(s.offeredAt)), lang) })}</span> : null}
                </li>
              ))}
            </ol>
            <div className="chips">
              <Button size="sm" disabled={busy || left === 0 || !nextUp} onClick={() => run({ what: 'offer', offer: { dayKey: d.dayKey } })} aria-describedby={left === 0 || !nextUp ? `offer-why-${a.activityId}-${d.dayKey}` : undefined} data-testid={`act-offer-${d.dayKey}`}>
                {words.button}
              </Button>
            </div>
            {left === 0 || !nextUp ? (
              <p className="help" id={`offer-why-${a.activityId}-${d.dayKey}`} data-testid={`act-offer-why-${d.dayKey}`}>
                {left === 0 ? t('svc183.act.offerFull') : t('svc183.act.offerAllAsked')}
              </p>
            ) : null}
          </div>
        ) : null}
        {past ? (
          d.closedOut ? (
            <span>{t('activities.hoursDone')}</span>
          ) : d.confirmed ? (
            <div className="chips">
              <Button size="sm" variant="secondary" onClick={onHours} data-testid={`act-hours-${d.dayKey}`}>
                {t('activities.hours')}
              </Button>
            </div>
          ) : null
        ) : d.confirmed < a.cap ? (
          <div className="chips">
            <Button size="sm" disabled={busy} onClick={() => run({ what: 'send', send: { dayKey: d.dayKey, audience: 'available' } })} data-testid={`act-send-${d.dayKey}`}>
              {t('activities.sendAvailable')}
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => run({ what: 'send', send: { dayKey: d.dayKey, audience: 'all' } })} data-testid={`act-send-all-${d.dayKey}`} aria-describedby={`act-all-help-${d.dayKey}`}>
              {t('activities.sendAll')}
            </Button>
            <span className="sr-only" id={`act-all-help-${d.dayKey}`}>
              {t('activities.sendAllHelp')}
            </span>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function HoursSheet({ a, d, onClose }: { a: ActivityView; d: ActivityDateView; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useActivityRefresh();
  const confirmed = d.helpers.filter((h) => h.state === 'confirmed');
  const [rows, setRows] = useState(confirmed.map((h) => ({ volunteerId: h.volunteerId, name: h.name, came: true, minutes: String(a.duration) })));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await pressActivity<{ message: string }>(a.activityId, { what: 'hours', hours: { dayKey: d.dayKey, entries: rows.map((x) => ({ volunteerId: x.volunteerId, came: x.came, minutes: x.minutes.trim() ? Math.max(0, Math.floor(Number(x.minutes) || 0)) : null })) } });
      toast(r.message);
      announce(r.message);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setProblem(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent
      title={t('activities.hoursTitle', { name: a.name, day: formatDayMedium(d.dayKey, i18n.language) })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('activities.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => save()} data-testid="act-hours-save">
            {t('activities.hoursSave')}
          </Button>
        </>
      }
    >
      <p className="help">{t('activities.hoursLead')}</p>
      {rows.map((x, i) => (
        <fieldset key={x.volunteerId} className="field">
          <legend className="eyebrow">{x.name}</legend>
          <label className="switch">
            <input type="checkbox" checked={x.came} onChange={(e) => setRows(rows.map((y, k) => (k === i ? { ...y, came: e.target.checked } : y)))} />
            <span>{t('activities.came')}</span>
          </label>
          {x.came ? (
            <div className="field">
              <label htmlFor={`act-min-${i}`}>{t('activities.minutes')}</label>
              <input id={`act-min-${i}`} className="input" inputMode="numeric" value={x.minutes} onChange={(e) => setRows(rows.map((y, k) => (k === i ? { ...y, minutes: e.target.value } : y)))} />
            </div>
          ) : null}
        </fieldset>
      ))}
      {problem ? (
        <p className="field-err" role="alert">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}

/** Today → Activities today (1.5.1): today's confirmed Event & activity support — only when there is one; never mixed with the visits. */
export function ActivitiesToday() {
  const { t } = useTranslation();
  const list = useActivities();
  const today = list.data?.today ?? [];
  if (!today.length) return null;
  return (
    <Card as="section" aria-labelledby="h-act-today" style={{ marginTop: 18 }} data-testid="activities-today">
      <CardHeader>
        <CardTitle id="h-act-today">{t('activities.todayTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="needs">
          {today.map((a) => (
            <li key={a.eventId} data-testid={`act-today-${a.activityId}`}>
              <span className="status scheduled">
                <Icon name="volunteers" aria-hidden="true" /> {minutesLabel(a.minutes)}
              </span>
              <div>
                <b>{a.name}</b>
                <span>{t('activities.todayLine', { where: a.where, length: lengthWords(a.duration), who: a.volunteers.join(', ') })}</span>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
